import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Select, { StylesConfig } from 'react-select';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import { FaChevronDown } from 'react-icons/fa';

// Mirrors every field te_nonrecon.fmt's CONTROL block lets a user Enter-Query on and QUERY_BILLING
// [oid 26] actually uses - period_no is the one required field (the Pay Period main-bar control,
// matching :control.period_no); office is the second most commonly used, so it's also in the main
// bar (matching Employee Type's spot on the Time & Expense Checklist search). Everything else lives
// in Advanced Search. beg_date/end_date are deliberately excluded - they're read-only, auto-
// populated from period_no (KEY-NEXT-ITEM's own lookup), not independent search criteria.
export interface ITeNonReconSearchCriteria {
    periodNo: number;
    office?: string;
    tsdMgr?: number;
    clNo?: number;
    prNo?: number;
    status?: string;
}

interface IOptionType {
    value: string;
    label: string;
}

interface ITeNonReconSearchProps {
    selectedPayperiod?: string;
    setSelectedPayPeriod: (value: string) => void;
    setPayPeriodSearchTerm: (value: string) => void;
    payPeriods: Array<{ id: number | string; name: string }>;
    onSearch: (criteria: Omit<ITeNonReconSearchCriteria, 'periodNo'>) => void;
    onExpandAll: () => void;
    onCollapseAll: () => void;
}

// Matches OFFICE's own static poplist in te_nonrecon.fmt (not a live-queried list like the Time &
// Expense Checklist's Offices grid) - same office set InvoiceGroupingDialog's own Office filter
// uses.
const OFFICE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'ALL', label: '*' },
    { value: 'CO', label: 'Denver' },
    { value: 'IH', label: 'In-House' },
    { value: 'MD', label: 'Midland' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'OK', label: 'Oklahoma City' },
    { value: 'TX', label: 'Texas' },
    { value: 'RN', label: 'Renewables' },
];

// STATUS's own static poplist, confirmed directly from the .fmt (4 List Elements: All/ALL, No
// Invoice/NI, No Check/NC, Office - No Invoice/OI).
const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'ALL', label: 'All' },
    { value: 'NI', label: 'No Invoice' },
    { value: 'NC', label: 'No Check' },
    { value: 'OI', label: 'Office - No Invoice' },
];

// Matches the plain RHFInputBox `.field` look, same as EmployeePayrollSearch/CheckRegisterSearch's
// own Selects.
const selectStyles: StylesConfig<IOptionType, false> = {
    control: (provided) => ({
        ...provided,
        minHeight: '3rem',
        height: '3rem',
        borderRadius: 0,
        border: 'none',
        borderBottom: '1px solid grey',
        boxShadow: 'none',
    }),
    valueContainer: (provided) => ({ ...provided, height: '3rem', padding: '0px 8px' }),
    input: (provided) => ({ ...provided, margin: '0px', padding: '0px' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '3rem' }),
    // Without an explicit z-index, the open menu can render behind the CollapsibleSection panels'
    // own <thead> below it (their .project-billing CSS/position:relative context otherwise wins the
    // default stacking order) - 9999 clears any of this page's own content.
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '0px' }),
    clearIndicator: (provided) => ({ ...provided, padding: '0px' }),
};

const ADVANCED_FIELD_NAMES = ['tsdMgrSearch', 'clNoSearch', 'prNoSearch'];

// Same shape as TimeAndExpenseChecklistSearch: Period No (+ Office, the 2 most commonly used
// fields) stay in a simple, always-visible main bar; everything else lives in a floating "Advanced
// Search" panel toggled open/closed, auto-closed the moment Search runs. The floating panel's
// fields stay mounted at all times (hidden only via opacity/max-height, never conditional JSX) -
// react-hook-form's uncontrolled inputs would otherwise lose their value the instant they unmount.
const TeNonReconSearch = (props: ITeNonReconSearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

    // value is the period number itself (label with the dash stripped), not the pay period row's
    // surrogate id - the id and the real period number are different values, and it's the period
    // number the backend's periodNo route param expects.
    const periodOptions: Array<IOptionType> = (props.payPeriods ?? []).map((pp) => ({ value: pp.name.replace(/-/g, ''), label: pp.name }));
    const selectedPeriodOption = periodOptions.find((o) => o.value === props.selectedPayperiod) ?? null;

    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    const executeSearch = () => {
        const values = getValues();
        props.onSearch({
            office: values.officeSearch && values.officeSearch !== 'ALL' ? values.officeSearch : undefined,
            tsdMgr: toNumber(values.tsdMgrSearch),
            clNo: toNumber(values.clNoSearch),
            prNo: toNumber(values.prNoSearch),
            status: values.statusSearch && values.statusSearch !== 'ALL' ? values.statusSearch : undefined,
        });
        setIsAdvancedOpen(false);
    };

    const clearSearchFilters = () => {
        ADVANCED_FIELD_NAMES.forEach((field) => setValue(field, ''));
        setValue('officeSearch', 'ALL');
        setValue('statusSearch', 'ALL');
        props.setSelectedPayPeriod(undefined);
    };

    const fieldStyle = { width: '100%' };

    const renderField = (label: string, name: string, width = '15%') => (
        <div style={{ width, paddingLeft: '4px' }}>
            <Fieldset label={label} style={{ border: 0, padding: 0 }}>
                <RHFInputBox name={name} type="number" register={register} errors={errors} style={fieldStyle} bodyStyle={fieldStyle} />
            </Fieldset>
        </div>
    );

    const isAdvancedVisible = isAdvancedOpen && !isCollapsed;

    return (
        <div style={{ position: 'relative' }}>
            {isCollapsed ? (
                <div style={{ textAlign: 'center' }}>
                    <Button
                        text="(+) Search Filters"
                        className="blue-hover-button"
                        style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                        onClick={() => setIsCollapsed(false)}
                    />
                </div>
            ) : (
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        {/* Sized to its own content (fields + buttons), not stretched to match the
                            wider grid panels below - the main bar's row is flexWrap:'nowrap' with
                            fixed-width children, so fit-content shrinks exactly to them. */}
                        <div style={{ width: 'fit-content', margin: '0 auto' }}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle="Non-Reconciled Time & Expense Search">
                                    <form>
                                        <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                            <div style={{ width: '15rem', flexShrink: 0 }}>
                                                <Fieldset label="Pay Period" style={{ border: 0, padding: 0 }}>
                                                    <Select
                                                        isClearable={true}
                                                        // Without this, the control keeps focus after a selection with its own
                                                        // (uncontrolled) typed-search text still empty - react-select's default
                                                        // behavior in that state is to show the placeholder instead of the
                                                        // selected label, even though the actual selected value is correct the
                                                        // whole time. Blurring immediately restores the "closed" display.
                                                        blurInputOnSelect={true}
                                                        inputId="teNonReconPeriodSearchInput"
                                                        placeholder="Pay Period"
                                                        options={periodOptions}
                                                        value={selectedPeriodOption}
                                                        onChange={(val: IOptionType | null) => props.setSelectedPayPeriod(val?.value)}
                                                        // react-select also calls onInputChange('') right after a selection is made (action
                                                        // "set-value", not a real keystroke) - piping that through would re-run
                                                        // resetPayPeriodSearchOptions with an empty term, which drops all date-range
                                                        // filters and can refetch a page of pay periods that no longer includes the one
                                                        // just selected, making the field appear to revert to its placeholder even though
                                                        // selectedPayPeriod itself is still correct. Only forward genuine typing.
                                                        onInputChange={(inputText, actionMeta) => {
                                                            if (actionMeta.action === 'input-change') {
                                                                props.setPayPeriodSearchTerm(inputText);
                                                            }
                                                        }}
                                                        isSearchable={true}
                                                        styles={selectStyles}
                                                    />
                                                </Fieldset>
                                            </div>
                                            <div style={{ width: '14rem', paddingLeft: '1rem', flexShrink: 0 }}>
                                                <Fieldset label="Office" style={{ border: 0, padding: 0 }}>
                                                    <RHFSelect
                                                        name="officeSearch"
                                                        defaultVal="ALL"
                                                        options={OFFICE_OPTIONS}
                                                        register={register}
                                                        style={fieldStyle}
                                                        bodyStyle={fieldStyle}
                                                    />
                                                </Fieldset>
                                            </div>
                                            <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                                <Button
                                                    text="Search"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => executeSearch()}
                                                />
                                                <Button
                                                    text="(-) Minimize"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => setIsCollapsed(true)}
                                                />
                                                <Button
                                                    text="Clear"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => clearSearchFilters()}
                                                />
                                                <Button
                                                    text="Advanced Search"
                                                    reactIcon={FaChevronDown}
                                                    iconStyle={{
                                                        fontSize: '1.6rem',
                                                        marginRight: '.5rem',
                                                        transform: `scaleY(${isAdvancedOpen ? -1 : 1})`,
                                                        transition: 'transform .25s ease',
                                                        verticalAlign: 'middle',
                                                    }}
                                                    className="blue-hover-button"
                                                    style={{
                                                        whiteSpace: 'nowrap', marginLeft: '1rem', width: '14rem', height: '3rem',
                                                        verticalAlign: 'middle', borderRadius: '5px',
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                                />
                                                <Button
                                                    text="Expand All"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '10rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => props.onExpandAll()}
                                                />
                                                <Button
                                                    text="Collapse All"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '10rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => props.onCollapseAll()}
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </Panel>
                            </Panel>
                        </div>
                    </Page.Section>
                </Page>
            )}

            {/* Floating "Advanced Search" panel - same absolute-position-over-the-content pattern as
                TimeAndExpenseChecklistSearch's own. */}
            <div
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 40,
                    overflow: 'hidden',
                    maxHeight: isAdvancedVisible ? '2000px' : '0px',
                    opacity: isAdvancedVisible ? 1 : 0,
                    pointerEvents: isAdvancedVisible ? 'auto' : 'none',
                    transition: 'max-height .35s ease, opacity .25s ease',
                    boxShadow: '0 8px 16px rgba(0,0,0,.3)',
                }}
            >
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        {/* Kept at the grid panels' own width (not fit-content like the main bar
                            above) - its fields use percentage widths, which need a real resolved
                            container width to size against. */}
                        <div style={{ maxWidth: '130rem', margin: '0 auto' }}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle="Advanced Search">
                                    <form>
                                        <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                            {renderField('TSDI Mgr No.', 'tsdMgrSearch')}
                                            {renderField('Client No.', 'clNoSearch')}
                                            {renderField('Project No.', 'prNoSearch')}
                                            <div style={{ width: '20%', paddingLeft: '4px' }}>
                                                <Fieldset label="Type" style={{ border: 0, padding: 0 }}>
                                                    <RHFSelect
                                                        name="statusSearch"
                                                        defaultVal="ALL"
                                                        options={STATUS_OPTIONS}
                                                        register={register}
                                                        style={fieldStyle}
                                                        bodyStyle={fieldStyle}
                                                    />
                                                </Fieldset>
                                            </div>
                                        </div>
                                    </form>
                                </Panel>
                            </Panel>
                        </div>
                    </Page.Section>
                </Page>
            </div>
        </div>
    );
};

export default TeNonReconSearch;



// WEBPACK FOOTER //
// ./src/components/project-billing/components/TeNonReconSearch.tsx